import { Divider } from '@chakra-ui/react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import React from 'react';
import { useLocation } from 'react-router-dom';

import type { GetUserQueryGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

import { useOrganizationUiVisibility } from 'packages/common-frontend/hooks/useUiVisibility';
import { CollapsableSideBar } from 'packages/sidebar/ui/CollapsableSideBar';
import { SideBarItem } from 'packages/sidebar/ui/SideBarItem';

import 'source/borrower/borrower.scss';

export const BorrowerSideBar: React.FC<{
  userData: GetUserQueryGQLType;
}> = withLDConsumer()(({ userData, flags }) => {
  const { pathname: currentPath } = useLocation();

  // @JonAtFinley @danitian replace with real UiVisibility returned from auth
  const { sideBarItems: sideBarItemsVisibility } = useOrganizationUiVisibility({
    mockUiVisibilityMode: flags?.isBorrowerLimitedOrganization
      ? 'borrowerLimited'
      : 'borrowerOwner',
    organizationSlug: userData.user?.organization.slug,
  });

  if (flags?.enableManualUploaderOnly) {
    return (
      <CollapsableSideBar>
        <SideBarItem
          routeName="manual-uploader"
          currentPath={currentPath}
          label="Manual Uploader"
          icon="arrow-up-from-bracket"
        />
      </CollapsableSideBar>
    );
  }
  return (
    <CollapsableSideBar>
      {sideBarItemsVisibility.home && (
        <SideBarItem
          routeName=""
          currentPath={currentPath}
          label="Home"
          icon="house"
        />
      )}
      {sideBarItemsVisibility.creditAgreements && (
        <SideBarItem
          routeName="credit-agreements"
          currentPath={currentPath}
          label="Credit Agreements"
          icon="file-lines"
        />
      )}
      {sideBarItemsVisibility.reports && (
        <SideBarItem
          routeName="reports"
          currentPath={currentPath}
          label="Reports"
          icon="folder-arrow-down"
        />
      )}
      {!flags?.hideManualUploader && sideBarItemsVisibility.manualUploader && (
        <SideBarItem
          routeName="manual-uploader"
          currentPath={currentPath}
          label="Manual Uploader"
          icon="arrow-up-from-bracket"
        />
      )}
      {flags?.showBorrowerReceivableDashboard &&
        sideBarItemsVisibility.receivables && (
          <SideBarItem
            routeName="credit-facility-receivables"
            currentPath={currentPath}
            label="Receivables"
            icon="list"
          />
        )}
      {flags?.displayAssignment && sideBarItemsVisibility.assignments && (
        <SideBarItem
          routeName="assignments"
          currentPath={currentPath}
          label="Assignments"
          icon="sliders-up"
        />
      )}
      {sideBarItemsVisibility.fundingRequests && (
        <SideBarItem
          routeName="funding-requests"
          currentPath={currentPath}
          label="Funding Requests"
          icon="dollar-sign"
        />
      )}
      {flags?.showStratsPage &&
        flags.showStratsPage !== 'hide' &&
        sideBarItemsVisibility.strats && (
          <SideBarItem
            routeName="strats"
            currentPath={currentPath}
            label="Strats"
            icon="chart-mixed"
          />
        )}
      {sideBarItemsVisibility.insights && (
        <SideBarItem
          routeName="insights"
          currentPath={currentPath}
          label="Insights"
          icon="chart-pie-simple"
        />
      )}
      {sideBarItemsVisibility.statements && (
        <SideBarItem
          routeName="statements/outstanding-ledger"
          currentPath={currentPath}
          label="Statements"
          icon="money-check-dollar"
        />
      )}
      {sideBarItemsVisibility.deliverables && (
        <SideBarItem
          routeName="deliverables"
          currentPath={currentPath}
          label="Deliverables"
          icon="calendar"
        />
      )}
      <Divider style={{ marginTop: '24px', marginBottom: '24px' }} />
      {sideBarItemsVisibility.resources && (
        <SideBarItem
          routeName="calculation-terminologies"
          currentPath={currentPath}
          label="Calculation Terminologies"
          icon="book"
        />
      )}
      {/* Too hard to filter events - just hide the entire section if we're filtering facilities. */}
      {!flags?.creditFacilitySlugsToHide?.length &&
        sideBarItemsVisibility.events && (
          <SideBarItem
            routeName="events"
            currentPath={currentPath}
            label="Events Log"
            icon="clock"
          />
        )}
      {sideBarItemsVisibility.settings && (
        <SideBarItem
          routeName="settings"
          currentPath={currentPath}
          label="Settings"
          icon="gear"
        />
      )}
    </CollapsableSideBar>
  );
});
